import { useState, useEffect, useCallback } from "react";
import { BASE_URL } from "./constants";

import LoginScreen       from "./components/LoginScreen";
import Sidebar           from "./components/Sidebar";
import Dashboard         from "./components/Dashboard";
import JobsTab           from "./components/JobsTab";
import ApplicantsTab     from "./components/ApplicantsTab";
import CreateJobModal    from "./components/CreateJobModal";
import EditJobModal      from "./components/EditJobModal";
import ApplicantDetailModal from "./components/ApplicantDetailModal";
import { Toast }         from "./components/UI";

// ─── Helper: read auth from localStorage ─────────────────────────────────────
const loadAuth = () => {
  try {
    const token = localStorage.getItem("jp_token");
    const user  = JSON.parse(localStorage.getItem("jp_user") || "null");
    return { token, user };
  } catch {
    return { token: null, user: null };
  }
};

const saveAuth = (token, user) => {
  localStorage.setItem("jp_token", token);
  localStorage.setItem("jp_user", JSON.stringify(user));
};

const clearAuth = () => {
  localStorage.removeItem("jp_token");
  localStorage.removeItem("jp_user");
};

// ─── App ─────────────────────────────────────────────────────────────────────
export default function App() {
  const { token: savedToken, user: savedUser } = loadAuth();

  const [token, setToken] = useState(savedToken);
  const [user,  setUser]  = useState(savedUser);
  const [tab,   setTab]   = useState("dashboard");
  const [toast, setToast] = useState(null);

  // Data
  const [stats,  setStats]  = useState(null);
  const [jobs,   setJobs]   = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);

  // Loading flags
  const [jobsLoading,       setJobsLoading]       = useState(false);
  const [applicantsLoading, setApplicantsLoading] = useState(false);

  // Modals
  const [createModal,    setCreateModal]    = useState(false);
  const [editModal,      setEditModal]      = useState(null);   // job object
  const [applicantModal, setApplicantModal] = useState(null);  // app object

  // ── Toast ──
  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });
  }, []);

  // ── Auth headers ──
  const authHeader = useCallback(() => ({
    Authorization: `Bearer ${token}`,
  }), [token]);

  // ── Fetch helpers ──
  const fetchStats = useCallback(async () => {
    try {
      const res  = await fetch(`${BASE_URL}/agent/job/dashboard-stats`, { headers: authHeader() });
      const data = await res.json();
      if (data.success) setStats(data.data);
    } catch {}
  }, [authHeader]);

  const fetchJobs = useCallback(async () => {
    setJobsLoading(true);
    try {
      const res  = await fetch(`${BASE_URL}/agent/job/my-jobs?page=1&limit=50`, { headers: authHeader() });
      const data = await res.json();
      if (data.success) setJobs(data.data || []);
    } catch {}
    setJobsLoading(false);
  }, [authHeader]);

  const fetchApplicants = useCallback(async (jobId) => {
    setApplicantsLoading(true);
    try {
      const res  = await fetch(`${BASE_URL}/agent/job/applicants/${jobId}?page=1&limit=50`, { headers: authHeader() });
      const data = await res.json();
      if (data.success) setApplicants(data.data || []);
    } catch {}
    setApplicantsLoading(false);
  }, [authHeader]);

  // ── Bootstrap on login ──
  useEffect(() => {
    if (!token) return;
    fetchStats();
    fetchJobs();
  }, [token, fetchStats, fetchJobs]);

  // ── Fetch applicants when switching to applicants tab ──
  useEffect(() => {
    if (tab === "applicants" && selectedJob) {
      fetchApplicants(selectedJob._id);
    }
  }, [tab, selectedJob, fetchApplicants]);

  // ── Login / Logout ──
  const handleLogin = (t, u) => {
    saveAuth(t, u);
    setToken(t);
    setUser(u);
  };

  const handleLogout = () => {
    clearAuth();
    setToken(null);
    setUser(null);
    setTab("dashboard");
    setStats(null);
    setJobs([]);
    setApplicants([]);
    setSelectedJob(null);
  };

  // ── Close job ──
  const handleCloseJob = async (jobId) => {
    if (!window.confirm("Close this job? It will no longer be visible to applicants.")) return;
    try {
      const res  = await fetch(`${BASE_URL}/agent/job/close/${jobId}`, {
        method: "PATCH", headers: authHeader(),
      });
      const data = await res.json();
      if (data.success) { showToast("Job closed."); fetchJobs(); fetchStats(); }
      else showToast(data.message || "Failed to close job.", "error");
    } catch {
      showToast("Network error.", "error");
    }
  };

  // ── Delete job ──
  const handleDeleteJob = async (jobId) => {
    if (!window.confirm("Delete this job permanently? This cannot be undone.")) return;
    try {
      const res  = await fetch(`${BASE_URL}/agent/job/delete/${jobId}`, {
        method: "DELETE", headers: authHeader(),
      });
      const data = await res.json();
      if (data.success) { showToast("Job deleted."); fetchJobs(); fetchStats(); }
      else showToast(data.message || "Failed to delete job.", "error");
    } catch {
      showToast("Network error.", "error");
    }
  };

  // ── Applicant action callback ──
  const handleApplicantAction = (message, type) => {
    showToast(message, type);
    if (type !== "error") {
      setApplicantModal(null);
      if (selectedJob) fetchApplicants(selectedJob._id);
      fetchStats();
    }
  };

  // ── Create job callback ──
  const handleCreateJob = (message, type = "success") => {
    showToast(message, type);
    if (type !== "error") {
      setCreateModal(false);
      fetchJobs();
      fetchStats();
    }
  };

  // ── Edit job callback ──
  const handleEditJob = (message, type = "success") => {
    showToast(message, type);
    if (type !== "error") {
      setEditModal(null);
      fetchJobs();
    }
  };

  // ── Not logged in ──
  if (!token) return <LoginScreen onLogin={handleLogin} />;

  // ── Main shell ──
  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", fontFamily: "'DM Sans', sans-serif" }}>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      {/* Modals */}
      {createModal && (
        <CreateJobModal
          token={token}
          onClose={() => setCreateModal(false)}
          onSuccess={handleCreateJob}
        />
      )}
      {editModal && (
        <EditJobModal
          job={editModal}
          token={token}
          onClose={() => setEditModal(null)}
          onSuccess={handleEditJob}
        />
      )}
      {applicantModal && (
        <ApplicantDetailModal
          app={applicantModal}
          token={token}
          jobId={selectedJob?._id}
          onClose={() => setApplicantModal(null)}
          onSuccess={handleApplicantAction}
        />
      )}

      {/* Sidebar */}
      <Sidebar tab={tab} setTab={setTab} user={user} onLogout={handleLogout} />

      {/* Main content */}
      <div style={{
        marginLeft: 240, padding: "36px 36px 60px",
        animation: "fadeUp 0.35s ease",
        minHeight: "100vh",
      }}>
        {tab === "dashboard" && (
          <Dashboard
            user={user}
            stats={stats}
            jobs={jobs}
            onCreateJob={() => setCreateModal(true)}
            setTab={setTab}
            setSelectedJob={setSelectedJob}
          />
        )}

        {tab === "jobs" && (
          <JobsTab
            jobs={jobs}
            loading={jobsLoading}
            onCreateJob={() => setCreateModal(true)}
            onEditJob={(job) => setEditModal(job)}
            onCloseJob={handleCloseJob}
            onDeleteJob={handleDeleteJob}
            setSelectedJob={setSelectedJob}
            setTab={setTab}
          />
        )}

        {tab === "applicants" && (
          <ApplicantsTab
            jobs={jobs}
            applicants={applicants}
            selectedJob={selectedJob}
            loading={applicantsLoading}
            setSelectedJob={setSelectedJob}
            fetchApplicants={fetchApplicants}
            onReview={(app) => setApplicantModal(app)}
          />
        )}
      </div>
    </div>
  );
}